'use client';

import {State, useSWRConfig} from 'swr';
import {redeemGiftCard, verifyGiftCard} from './partner.fetch';
import {useCallback, useMemo} from 'react';
import {CatalogOfferProduct, ConnectedPlatform, GiftCardRedeem, RedeemPinType} from '@/types';
import {useCatalogOffers, useCurrencyDetail} from '../catalog';
import {formatCurrency} from '@/lib/utils/giftCard.utils';
import {useLocale} from '@epic-mw/i18n';
import useSWRMutation from 'swr/mutation';
import {refreshAccountTotalBalance} from '../wallet/wallet.hooks';
import {useCountry} from '../geo';
import {ErrorResponse} from '@/types/error.types';

/**
 * Fetching from GIFT CARD api below
 */

interface GiftCardOptions {
	onSuccess?: (data: GiftCardRedeem | undefined) => void;
	onError?: (error: string) => void;
}

/**
 * Fetching from GIFT CARD STORE below
 */
type GiftCardError = ErrorResponse<{pinType: RedeemPinType}>;
export interface GiftCardStore {
	verify?: State<GiftCardRedeem, GiftCardError>;
	redeem?: State<GiftCardRedeem, GiftCardError>;
}

export const GC_VERIFY_SWR_KEY = '/code/verify';
export const GC_REDEEM_SWR_KEY = '/code/redeem';

const useGiftCard = (
	key: string,
	fetcher: (code: string, locale: string, solveToken?: string) => Promise<GiftCardRedeem>,
	options?: GiftCardOptions
) => {
	const locale = useLocale();

	const handler = useCallback(
		async (url: string, {arg}: {arg: {newCode: string; solveToken?: string}}) => {
			if (!arg.newCode) {
				console.warn('No code provided...', arg.newCode);
				return undefined;
			}
			return fetcher(arg.newCode, locale, arg.solveToken);
		},
		[fetcher, locale]
	);

	const {trigger, ...query} = useSWRMutation(key, handler, {
		onError: (err: GiftCardError, _key, _config) => {
			const error = err?.error;
			options?.onError?.(error);
			return error;
		},
		onSuccess: (result, key, _config) => {
			if (key === GC_REDEEM_SWR_KEY) {
				refreshAccountTotalBalance().catch((err) => {
					console.error(err);
				});
			}
			options?.onSuccess?.(result);
		},
		populateCache: true,
		revalidate: false
	});

	return {
		...query,
		trigger,
		isMutating: query.isMutating
	};
};

export const useGiftCardVerify = (options?: GiftCardOptions) => {
	return useGiftCard(GC_VERIFY_SWR_KEY, verifyGiftCard, options);
};

export const useGiftCardRedeem = (options?: GiftCardOptions) => {
	const redeem = useGiftCard(GC_REDEEM_SWR_KEY, redeemGiftCard, options);
	return {
		...redeem,
		redeemed: !!redeem.error === false && !!redeem.data?.epicAccountId === true
	};
};

export const useGiftCardStore = (): GiftCardStore => {
	const {cache} = useSWRConfig();
	return {
		verify: cache.get(GC_VERIFY_SWR_KEY) as GiftCardStore['verify'],
		redeem: cache.get(GC_REDEEM_SWR_KEY) as GiftCardStore['redeem']
	};
};

export const useGiftCardVerified = () => {
	const store = useGiftCardStore();
	return {
		isLoading: store.verify?.isLoading,
		verified: !!(store.verify && store.verify.data?.epicAccountId && !store.verify.error)
	};
};

export const useGiftCardRedeemedAmount = () => {
	const locale = useLocale();
	const {redeem = {}} = useGiftCardStore();
	const redeemed = !!redeem.error === false && !!redeem.data?.epicAccountId === true;
	const currencyCode = redeem.data?.currency || '';
	const currencyDetail = useCurrencyDetail(currencyCode);
	const decimals = currencyDetail?.currency?.decimals || 0;

	if (!redeemed) {
		return null;
	}

	return formatCurrency(redeem.data?.amount || 0, currencyCode, locale, decimals);
};

export const useGiftCardEulaPrompt = () => {
	const {verify = {}} = useGiftCardStore();
	const eula = verify.data?.explicitEulaPromptsRequired?.[0];
	return {...eula!, show: typeof eula === 'object'};
};

export const useConnectedPlatforms = (): ConnectedPlatform[] => {
	const store = useGiftCardStore();
	return store?.verify?.data?.connectedPlatforms || [];
};

export const useGiftCardAmounts = () => {
	const locale = useLocale();
	const {redeem, verify} = useGiftCardStore();
	const verifyData = verify?.data || ({} as GiftCardRedeem);

	if (!verifyData?.currency) {
		return {
			isLoading: false,
			maxBalance: 0,
			currentBalance: 0,
			cardAmount: 0,
			newBalance: 0
		};
	}

	const {
		amount = verifyData.amount,
		currency: currencyCode = verifyData.currency,
		walletDetails = verifyData.walletDetails
	} = redeem?.data || {};

	const currencyDetail = useCurrencyDetail(currencyCode);
	const decimals = currencyDetail?.currency?.decimals || 0;

	const format = (amount: number) => {
		return formatCurrency(amount || 0, currencyCode, locale, decimals);
	};

	const maxBalance = walletDetails?.maxAllowedBalance || 0;
	const currentBalance = walletDetails?.balanceBefore || 0;
	const cardAmount = amount || 0;
	const newBalance = currentBalance + cardAmount;

	return {
		isLoading: currencyDetail.isLoading,
		maxBalance: format(maxBalance),
		currentBalance: format(currentBalance),
		cardAmount: format(cardAmount),
		newBalance: format(newBalance)
	};
};

export const useGiftCardQualifiedPromotions = () => {
	const locale = useLocale();
	const country = useCountry();
	const {redeem} = useGiftCardStore();
	const qualifiedPromotions = redeem?.data?.qualifiedPromotions || [];

	const products = useMemo<CatalogOfferProduct[]>(() => {
		if (!qualifiedPromotions?.length) {
			return [];
		}
		return Object.entries(
			qualifiedPromotions
				.flatMap((p) => p.offers)
				.reduce(
					(acc, {namespace, offerId}) => {
						(acc[namespace] ??= new Set()).add(offerId);
						return acc;
					},
					{} as Record<string, Set<string>>
				)
		).map(([sandboxId, offerIds]) => ({sandboxId, offerIds: [...offerIds]}));
	}, [redeem?.data?.pin]);

	return useCatalogOffers(products, locale, country);
};
